import { FormlyFieldConfig } from '@ngx-formly/core';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { FormlyExtendedControlToOverride } from './formly-base-config.model';

export abstract class FormlyBaseConfigService<
  TConfig extends {
    propertiesToOverride: Record<string, any>;
    fieldsToHide?: string[];
    extendedPropertiesToOverride?: FormlyExtendedControlToOverride<any, any>[];
  },
  TModel,
  TControlNames extends string,
> {
  protected formModelCurrentState = new BehaviorSubject<TModel>(null as any);
  protected config = new BehaviorSubject<TConfig>(null as any);

  emitConfig(config: TConfig): void {
    this.config.next(config);
  }

  getConfig(): Observable<TConfig> {
    return this.config.asObservable();
  }

  getFieldsWithCustomConfig(
    fields: FormlyFieldConfig[],
    getLabelFunction: (key: string) => Observable<string>,
  ): Observable<FormlyFieldConfig[]> {
    return this.getConfig().pipe(
      map((config) => {
        const processField = (field: FormlyFieldConfig): FormlyFieldConfig => {
          if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
            field.fieldGroup = field.fieldGroup.map(processField);
          } else {
            const key = field.key as TControlNames;
            const propertiesToOverride =
              config?.propertiesToOverride[key as string];
            if (key) {
              field.expressions = {
                ...this.getCommonExpressions(
                  key,
                  getLabelFunction,
                  propertiesToOverride?.['labelTranslationKey'] as string,
                ),
                ...(field.expressions || {}),
              };
            }
            if (propertiesToOverride) {
              Object.keys(propertiesToOverride).forEach((overrideKey) => {
                _.set(field, overrideKey, propertiesToOverride[overrideKey]);
              });
            }
            const extendedPropertiesToOverride =
              config?.extendedPropertiesToOverride?.find(
                (property) => property.controlName === key,
              )?.properties;
            if (extendedPropertiesToOverride) {
              extendedPropertiesToOverride.forEach((property) => {
                _.set(field, property.pathToProperty, property.value);
              });
            }
          }
          return field;
        };
        return fields.map(processField);
      }),
    );
  }

  getCommonExpressions(
    controlKey: TControlNames,
    getLabelFunction: (key: string | TControlNames) => Observable<string>,
    customLabelTranslationKey?: string,
  ): {
    'props.label': Observable<string>;
    hide: Observable<boolean>;
  } {
    return {
      'props.label': getLabelFunction(customLabelTranslationKey || controlKey),
      hide: this.shouldFieldBeHidden(controlKey),
    };
  }

  shouldFieldBeHidden(field: TControlNames): Observable<boolean> {
    return this.getConfig().pipe(
      map((config) =>
        config?.fieldsToHide?.includes(field as string) ? true : undefined,
      ),
    );
  }

  saveFormModel(formModel: TModel): void {
    this.formModelCurrentState.next(formModel);
  }

  getFormModel(): Observable<TModel> {
    return this.formModelCurrentState.asObservable();
  }

  createFieldWithDefaults = (defaultConfig: FormlyFieldConfig) => {
    return (field: FormlyFieldConfig): FormlyFieldConfig => ({
      ...defaultConfig,
      ...field,
      props: {
        ...(defaultConfig.props || {}),
        ...(field.props || {}),
        attributes: {
          ...(defaultConfig.props?.attributes || {}),
          ...(field.props?.attributes || {}),
        },
      },
    });
  };

  findFieldByKey = (
    fields: FormlyFieldConfig[],
    key: string,
  ): FormlyFieldConfig | undefined =>
    fields
      .flatMap((field) =>
        field.key === key
          ? [field]
          : field.fieldGroup
            ? [this.findFieldByKey(field.fieldGroup, key)]
            : [],
      )
      .find(Boolean);
}
